import { createSelector, createSlice } from "@reduxjs/toolkit";
import {
    getClassificationInitialState,
    getRegistrationsInitialState,
} from "../utilities/initialStateHelpers";
import {
    getAddItemToRegistrationStoreCart,
    getAddRegistration,
    getRemoveRegistration,
    getRemoveRegistrationStoreCartItem,
    getUpdateCategoryOnRegistrations,
    getUpdateRegistration,
    getUpdateRegistrationStoreCart,
    getUpdateRegistrationTotals,
} from "../actions/registrationActions/registrationActions";

const getInitialState = () => {
    const registrationInitialState = {
        allowEarlyFormSubmissionRegistrationPage: false,
        allowEarlyFormSubmissionStorePage: false,
        // TODO: currently using a function in registrationReducer to determine IS
        classification: getClassificationInitialState(),
        currentRegistration: 0,
        currentRegistrationCategory: [],
        payment: {},
        promoCodes: [],
        promoDiscounts: {},
        // TODO: currently using a function in registrationReducer to determine IS
        registrations: getRegistrationsInitialState(),
        totals: {
            giftAssistAmount: 0.0,
            giftsSubtotalAmount: 0.0,
            optionalDonationAmount: 0.0,
            prePromoRegistrantTotal: 0.0,
            prePromoTransTotal: 0.0,
            registrantSubtotal: 0.0,
            shippingMethodAmount: 0.0,
            storeTaxAmount: 0.0,
            storeSubtotalAmount: 0.0,
            total: 0.0,
        },
        storeCart: [],
        giftAssist: false,
        addAdditionalParticipantDisabled: false,
    };
    return registrationInitialState;
};

export const slice = createSlice({
    name: "registration",
    initialState: getInitialState(),
    reducers: {
        addRegistration: (state, action) => getAddRegistration(state, action),

        removeRegistration: (state, action) =>
            getRemoveRegistration(state, action),

        updateRegistration: (state, action) =>
            getUpdateRegistration(state, action),

        updateCurrentRegistration: (state, action) => {
            state.currentRegistration = action.payload;
        },
        updatePayment: (state, action) => {
            state.payment = action.payload;
        },
        // -------------------------------------------------------------------------
        // NOTE: I believe this belongs in productSlice but need to confirm
        // -------------------------------------------------------------------------
        // updateProducts,
        updatePromoCodes: (state, action) => {
            state.promoCodes = action.payload;
        },
        updatePromoDiscounts: (state, action) => {
            state.promoDiscounts = action.payload;
        },
        updateAddAdditionalParticipantDisabled: (state, action) => {
            state.addAdditionalParticipantDisabled = action.payload;
        },
        updateRegistrationGiftAssist: (state, action) => {
            state.giftAssist = action.payload;
        },
        updateRegistrationShippingMethod: (state, action) => {
            state.shippingMethod = action.payload;
        },
        updateRegistrationClassification: (state, action) => {
            state.classification = action.payload;
        },
        addItemToRegistrationStoreCart: (state, action) =>
            getAddItemToRegistrationStoreCart(state, action),

        updateRegistrationStoreCart: (state, action) =>
            getUpdateRegistrationStoreCart(state, action),

        removeRegistrationStoreCartItem: (state, action) =>
            getRemoveRegistrationStoreCartItem(state, action),

        updateRegistrationTotals: (state, action) =>
            getUpdateRegistrationTotals(state, action),

        setCurrentRegistrationCategory: (state, action) => {
            state.currentRegistrationCategory = action.payload;
        },

        setAllowEarlyFormSubmissionRegistrationPage: (state, action) => {
            state.allowEarlyFormSubmissionRegistrationPage = action.payload;
        },

        setAllowEarlyFormSubmissionStorePage: (state, action) => {
            state.allowEarlyFormSubmissionStorePage = action.payload;
        },

        updateCategoryOnRegistrations: (state, action) =>
            getUpdateCategoryOnRegistrations(state, action),

        replaceRegistrationState: (state, action) => action.payload,

        replaceRegistrationsOnRegistration: (state, action) => {
            state.registrations = action.payload;
        },

        updateCurrentRegistrationCategoryAndCurrentRegistration: (
            state,
            action,
        ) => {
            const { newCurrentRegistration, newCurrentRegistrationCategory } =
                action.payload;
            return {
                ...state,
                // should be an array
                currentRegistrationCategory: newCurrentRegistrationCategory,
                currentRegistration: newCurrentRegistration,
            };
        },
    },
});

// actions
export const {
    addRegistration,
    removeRegistration,
    updateCurrentRegistration,
    updatePayment,
    updatePromoCodes,
    updatePromoDiscounts,
    updateRegistration,
    updateAddAdditionalParticipantDisabled,
    updateRegistrationGiftAssist,
    updateRegistrationShippingMethod,
    updateRegistrationClassification,
    addItemToRegistrationStoreCart,
    updateRegistrationStoreCart,
    removeRegistrationStoreCartItem,
    updateRegistrationTotals,
    setCurrentRegistrationCategory,
    setAllowEarlyFormSubmissionRegistrationPage,
    setAllowEarlyFormSubmissionStorePage,
    updateCategoryOnRegistrations,
    replaceRegistrationState,
    replaceRegistrationsOnRegistration,
    updateCurrentRegistrationCategoryAndCurrentRegistration,
} = slice.actions;

// selectors

// same as getRegistration
export const selectAllRegistration = (state) => state.registration;

export const selectCurrentRegistrationIndex = (state) =>
    state.registration.currentRegistration;

export const selectRegistrationsFromRegistration = (state) =>
    state.registration.registrations;

export const selectRegistrationTotals = (state) => state.registration.totals;

export const selectUnconfirmedRegistrations = createSelector(
    [(state) => selectAllRegistration(state)],
    (registration) => {
        const { registrations = [], currentRegistration = 0 } = registration;

        const totalUnconfirmedRegistrations = registrations
            .map((mappedRegistration, i) => {
                const { Needs_To_Be_Reviewed = false } = mappedRegistration;
                if (Needs_To_Be_Reviewed) {
                    return { registrationIndex: i };
                }
                return false;
            })
            .filter((filteredRegistration) => filteredRegistration !== false);

        const remainingUnconfirmedRegistrations =
            totalUnconfirmedRegistrations.filter(
                (remainingRegistration) =>
                    remainingRegistration.registrationIndex !==
                    currentRegistration,
            );

        return {
            totalUnconfirmedRegistrations,
            remainingUnconfirmedRegistrations,
        };
    },
);

// getRegistrationClosed
// needs data from formSettings, categories, p2pSettings and classification
// Should the BE be determining if a form is Closed like Events and PDR?

export default slice.reducer;
